/**
 * Authentication context.
 *
 * The session is an httpOnly cookie the browser carries and this code cannot
 * read — which is the point. The old build kept a user id in localStorage and
 * trusted it; anything on the page could read it, change it, or invent one.
 * Now `restoreSession` asks the server who the caller is, and the server is
 * the only thing that can answer.
 *
 * Modules are hydrated here too, immediately after the identity is known. They
 * have to be loaded before the portal renders (its readers are synchronous),
 * and this is the one place that knows when a session begins and ends.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Role, User } from '../types';
import { userService } from '../services/userService';
import { moduleService } from '../services/moduleService';
import { roleService } from '../services/roleService';
import { setUnauthorisedHandler } from '../services/apiClient';
import { can, type Permission } from './permissions';

interface AuthContextValue {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  /** Re-reads the signed-in user (after an Admin edits their own record). */
  refreshCurrentUser: () => Promise<void>;
  role: Role | undefined;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const clearLocalSession = useCallback(() => {
    moduleService.reset();
    roleService.reset();
    setCurrentUser(null);
  }, []);

  /* Any request answering 401 means the session ended somewhere else — it
     expired, an admin disabled the account, or the password was changed. The
     app drops to the sign-in screen instead of leaving a shell whose every
     panel quietly fails. */
  useEffect(() => {
    setUnauthorisedHandler(clearLocalSession);
    return () => setUnauthorisedHandler(null);
  }, [clearLocalSession]);

  useEffect(() => {
    const restoreSession = async () => {
      const user = await userService.currentUser();
      if (user) {
        setCurrentUser(user);
        try {
          // Roles alongside modules: `can()` reads roleService's cache
          // synchronously (see auth/permissions.ts), so it needs to be
          // populated before the portal renders, same as modules do.
          await Promise.all([moduleService.hydrate(), roleService.hydrate()]);
        } catch {
          // The identity is good but one of the two did not load. Staying
          // signed in with an empty portal (or with every `can()` check
          // failing closed) is better than bouncing someone back to a login
          // screen their password will not fix.
          console.error('[modules/roles] could not be loaded on restore');
        }
      }
      setLoading(false);
    };
    void restoreSession();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const user = await userService.authenticate(email, password);
    if (!user) throw new Error('Invalid email or password');
    setCurrentUser(user);
    await Promise.all([moduleService.hydrate(), roleService.hydrate()]);
    return user;
  }, []);

  const logout = useCallback(() => {
    // Cleared locally first so the UI never shows another user's modules for
    // the moment the request is in flight; the server revokes the session too,
    // which is what actually ends it.
    clearLocalSession();
    void userService.logout();
  }, [clearLocalSession]);

  const refreshCurrentUser = useCallback(async () => {
    if (!currentUser) return;
    const fresh = await userService.currentUser();
    if (!fresh || fresh.status === 'disabled') {
      clearLocalSession();
      return;
    }
    setCurrentUser(fresh);
  }, [currentUser, clearLocalSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      loading,
      login,
      logout,
      refreshCurrentUser,
      role: currentUser?.role,
      can: (permission: Permission) => can(permission, currentUser?.role),
    }),
    [currentUser, loading, login, logout, refreshCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/** Convenience hook so components never compare roles directly. */
export function usePermissions() {
  const { role, can: hasPermission } = useAuth();
  return {
    role,
    can: hasPermission,
    isAdmin: role === 'admin',
  };
}
